import Button from '@mui/material/Button'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { message } from 'services/snackbar'
import { StoreThunkDispatch } from 'store/state'
import { getVideoThumbnailFromUrl } from 'utils/file/getVideoThumbnailFromUrl'
import styles from './VideoThumbnailGenerator.pcss'

interface VideoThumbnailGeneratorProps {
  videoFile: File
  onGenerateThumb: (thumb: string, offset: number | undefined) => void
  /** Appended to the explanation shown when the browser cannot play the video. */
  unsupportedHint?: string
}

export function VideoThumbnailGenerator(props: VideoThumbnailGeneratorProps) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [videoUrl, setVideoUrl] = useState<string>()
  const [currentTime, setCurrentTime] = useState('')
  const [ready, setReady] = useState(false)
  const [unsupported, setUnsupported] = useState(false)
  const [capturing, setCapturing] = useState(false)

  useEffect(() => {
    const url = URL.createObjectURL(props.videoFile)
    setVideoUrl(url)
    setReady(false)
    setUnsupported(false)
    return () => URL.revokeObjectURL(url)
  }, [props.videoFile])

  // EXPL: loadeddata also fires for a file whose audio track plays but whose video track the browser
  // cannot decode (e.g. ProRes, or HEVC without platform support) - only a non-zero frame size proves frames exist.
  const onLoadedData = () => {
    const video = videoRef.current
    if (video?.videoWidth && video.videoHeight) {
      setReady(true)
    } else {
      setUnsupported(true)
    }
  }

  const onTimeUpdate = () => {
    const video = videoRef.current
    if (video) {
      setCurrentTime(video.currentTime.toFixed(2))
    }
  }

  // EXPL: Frames are captured from a separate video element that seeks and waits for `seeked`, instead of drawing
  // the visible player - drawing the player before it has presented a frame yields a blank (uploaded as black) image.
  const generateThumbnail = async () => {
    const video = videoRef.current
    if (!video || !videoUrl) {
      return
    }

    video.pause()
    const offset = video.currentTime
    setCapturing(true)
    try {
      const thumb = await getVideoThumbnailFromUrl(videoUrl, offset)
      props.onGenerateThumb(thumb, offset)
    } catch {
      dispatch(message('Could not capture this frame. Please try again or pick a different frame.', 'error'))
    } finally {
      setCapturing(false)
    }
  }

  return (
    <div className={styles.container}>
      <Button
        variant="contained"
        color="primary"
        className={styles.btn}
        disabled={!ready || capturing}
        onClick={generateThumbnail}
      >
        Select as thumbnail{currentTime ? ' - ' + currentTime : ''}
      </Button>
      <video
        ref={videoRef}
        src={videoUrl}
        controls
        className={styles.video}
        data-testid="thumbnail-video"
        onLoadedData={onLoadedData}
        onError={() => setUnsupported(true)}
        onTimeUpdate={onTimeUpdate}
      >
        Your browser does not support the video tag.
      </video>
      {unsupported && (
        <p className={styles.unsupported} data-testid="thumbnail-unsupported">
          Your browser can't play this video format, so a thumbnail can't be picked from it.
          {props.unsupportedHint ? ` ${props.unsupportedHint}` : ''}
        </p>
      )}
    </div>
  )
}

export default VideoThumbnailGenerator
